import { useEffect, useState } from "react";
import Pagination from "../../components/common/pagination/Pagination";
import Title from "../../components/common/title/Title";
import Button from "../../components/common/button/Button";
import { getApi, deleteApi, putApi } from "../../services/api/globalApi";
import { useNavigate } from "react-router";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RxDragHandleDots2 } from "react-icons/rx";
import {
  successMessage,
  errorMessage,
} from "../../utils/toastify-message/ToastifyMessage";

const TYPE_LABEL = { voice: "صوتی", text: "متنی", screenshot: "تصویری", video: "ویدیویی" };

function avatarLabel(name) {
  return name ? [...name.trim()][0] : "؟";
}

function SortableItem({ item, index, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
    >
      <td className="px-3 py-3">
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="cursor-grab text-gray-400 hover:text-gray-600"
        >
          <RxDragHandleDots2 size={18} />
        </button>
      </td>
      <td className="px-3 py-3">
        <span
          className="inline-flex w-9 h-9 rounded-full items-center justify-center text-white font-bold text-sm"
          style={{ backgroundColor: item.avatarBg || "#2E9C57" }}
        >
          {avatarLabel(item.name)}
        </span>
      </td>
      <td className="px-3 py-3 font-medium text-gray-900 dark:text-white">
        {item.name}
      </td>
      <td className="px-3 py-3 text-sm text-gray-500">{item.city}</td>
      <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
        {TYPE_LABEL[item.type] || item.type}
      </td>
      <td className="px-3 py-3 text-sm text-gray-500 max-w-[180px] truncate">
        {item.quote}
      </td>
      <td className="px-3 py-3 text-center text-sm">{index + 1}</td>
      <td className="px-3 py-3">
        <span
          className={`px-2 py-1 rounded text-xs ${
            item.isActive
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {item.isActive ? "فعال" : "غیرفعال"}
        </span>
      </td>
      <td className="px-3 py-3">
        <div className="flex gap-3">
          <button
            onClick={() => onEdit(item)}
            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            ویرایش
          </button>
          <button
            onClick={() => onDelete(item)}
            className="text-sm font-medium text-red-600 dark:text-red-400 hover:underline"
          >
            حذف
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function TestimonialList() {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [filter, setFilter] = useState({});
  const sensors = useSensors(useSensor(PointerSensor));
  const navigate = useNavigate();

  const fetchData = async (params = {}) => {
    try {
      const res = await getApi({ path: "admin/affiliate-testimonials", params });
      const items = res?.data?.result?.data?.map((item) => ({
        ...item,
        id: item._id,
      }));
      setData(items || []);
      setPagination(res?.data?.result);
      setHasChanges(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData({});
  }, []);

  const filterDataHandler = (newParams) => {
    const merged = { ...filter, ...newParams };
    setFilter(merged);
    fetchData(merged);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = data.findIndex((i) => i.id === active.id);
      const newIndex = data.findIndex((i) => i.id === over?.id);
      setData((items) => arrayMove(items, oldIndex, newIndex));
      setHasChanges(true);
    }
  };

  const savePriorities = async () => {
    try {
      const items = data.map((item, index) => ({
        id: item._id,
        priority: data.length - index,
      }));
      await putApi({
        path: "admin/affiliate-testimonials/update-priorities",
        body: { items },
      });
      successMessage("اولویت‌ها ذخیره شد");
      setHasChanges(false);
      fetchData({});
    } catch {
      errorMessage("خطا در ذخیره اولویت‌ها");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`حذف نظر "${item.name}"؟`)) return;
    try {
      await deleteApi({ path: `admin/affiliate-testimonials/${item._id}` });
      successMessage("نظر حذف شد");
      fetchData(filter);
    } catch {
      errorMessage("خطا در حذف");
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
      <div className="flex justify-between items-center mb-6 gap-4">
        <Title title="نظرات نمایندگان" />
        <div className="flex gap-2">
          {hasChanges && (
            <Button
              onClick={savePriorities}
              text="ذخیره اولویت‌ها"
              color="primary"
              type="button"
              className="whitespace-nowrap min-w-fit"
            />
          )}
          <Button
            onClick={() => navigate("/add-affiliate-testimonial")}
            text="افزودن نظر"
            color="success"
            type="button"
            className="whitespace-nowrap min-w-fit"
          />
        </div>
      </div>

      <Pagination pagination={pagination} filterDataHandler={filterDataHandler} />

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg mt-6">
        {data.length ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full text-xs sm:text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-3 w-8"></th>
                  <th className="px-3 py-3 w-10">آواتار</th>
                  <th className="px-3 py-3">نام</th>
                  <th className="px-3 py-3">شهر</th>
                  <th className="px-3 py-3">نوع</th>
                  <th className="px-3 py-3">نظر</th>
                  <th className="px-3 py-3 text-center w-16">اولویت</th>
                  <th className="px-3 py-3">وضعیت</th>
                  <th className="px-3 py-3">عملیات</th>
                </tr>
              </thead>
              <SortableContext
                items={data.map((i) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {data.map((item, index) => (
                    <SortableItem
                      key={item.id}
                      item={item}
                      index={index}
                      onEdit={(i) =>
                        navigate("/edit-affiliate-testimonial", { state: i })
                      }
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        ) : (
          <div className="flex items-center justify-center py-10 dark:text-white">
            <p>هیچ نظری ثبت نشده است.</p>
          </div>
        )}
      </div>

      <Pagination pagination={pagination} filterDataHandler={filterDataHandler} />
    </div>
  );
}